import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import CloudStorage from "../../../../CloudStorage/CloudStorage";
import { rentalPriceDetails, rentalRequestListings } from "../../../../redux/rental/action";
import { Commas } from "../../../Common/AllDateTimeReleventFormate";
import { Tooltip } from "react-tooltip";
import TimePickerRSuite from "../../../Common/TimePickerRSuite";
import AsteriskLabel from "../../../Common/AsteriskLabel";

export default function RentProductSubmitRequestPopup({ data, setModaltitle }) {
  const laung = localStorage.getItem("laung");
  const [lan, setLan] = useState(laung ?? "en");
  const [totalprice, setTotalPrice] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();

  // toISOString() guarantees we get the current date in UTC
  const Today = new Date();
  const formattedDate = Today.toISOString().split("T")[0];

  const validationSchema = Yup.object({
    type: Yup.string().required("Type is required"),
    rental_id: Yup.string().required("Rental ID is required"),
    rental_type: Yup.string().required("Rental type is required"),
    start_date: Yup.date().required("Start date is required").nullable(),
    start_time: Yup.string()
      .required("Start time is required")
      .test(
        "not-past-time",
        "Start time cannot be in the past",
        function (value) {
          const { start_date } = this.parent;
          if (!value || !start_date) return true;

          const now = new Date();
          const currentUTCDate = now.toISOString().split("T")[0];

          // Check if selected date is today in UTC
          const isToday = start_date === currentUTCDate;

          if (!isToday) return true; // Skip time validation for future dates

          // Append 'Z' to treat the constructed string as a strict UTC timestamp
          const selectedDateTime = new Date(`${start_date}T${value}:00Z`);

          return selectedDateTime >= now;
        }
      ),
    end_date: Yup.date()
      .required("End date is required")
      .nullable()
      .test(
        "end-date-after-start-date",
        "End date must be later than or equal to start date",
        function (value) {
          const { start_date } = this.parent;
          if (!start_date || !value) return true;

          // Format value in case Yup parsed it into a full Date object
          const endDateStr = typeof value === "string" ? value : value.toISOString().split("T")[0];

          // Compare both dates explicitly at UTC midnight to avoid local timezone offset bugs
          return new Date(`${endDateStr}T00:00:00Z`) >= new Date(`${start_date}T00:00:00Z`);
        }
      ),
    end_time: Yup.string()
      .required("End time is required")
      .test(
        "end-time-after-start-time",
        "End time must be after start time when dates are the same",
        function (value) {
          const { start_date, end_date, start_time } = this.parent;
          if (!value || !start_time || !start_date || !end_date) return true;

          // Ensure end_date is a string formatted as YYYY-MM-DD
          const endDateStr = typeof end_date === "string" ? end_date : end_date.toISOString().split("T")[0];

          const isSameDay = start_date === endDateStr;

          if (!isSameDay) return true; // No time comparison needed if days differ

          // Append 'Z' to compare both times strictly in UTC
          const start = new Date(`${start_date}T${start_time}:00Z`);
          const end = new Date(`${endDateStr}T${value}:00Z`);

          return end > start;
        }
      ),
  });

  const dispatch = useDispatch();

  const initialRentalType = (() => {
    if (data?.rental_only == 1 && data?.rental_with_interpreter == 1) {
      return "1";
    } else if (data?.rental_only == 1) {
      return "1";
    } else if (data?.rental_with_interpreter == 1) {
      return "2";
    }
    return "";
  })();

  const formik = useFormik({
    initialValues: {
      type: "1",
      rental_id: id,
      rental_type: initialRentalType,
      rental_with_interpreter: "",
      start_date: "",
      start_time: "",
      end_date: "",
      end_time: "",
    },
    validationSchema,
    onSubmit: (values) => {
      console.log("Form values:", values);
      rentalContract(values);
    },
  });

  const rentalContract = (values) => {
    navigate(`/${lan}/rentalcontract/${id}`, {
      state: { approvefrom: "buyerfromlist", type: "rental", payload: { ...values, total_price: totalprice } },
    });
    setModaltitle(false);
  };

  useEffect(() => {
    if (
      formik?.values?.rental_type &&
      formik?.values?.start_date &&
      formik?.values?.start_time &&
      formik?.values?.end_date &&
      formik?.values?.end_time
    ) {
      calculatePrice(
        formik?.values?.rental_type,
        formik?.values?.start_date,
        formik?.values?.start_time,
        formik?.values?.end_date,
        formik?.values?.end_time
      );
    }
  }, [formik.values]);

  const calculatePrice = (rentalType, startDate, startTime, endDate, endTime) => {
    const payload = {
      type: "2",
      rental_id: id,
      rental_type: rentalType,
      start_date: startDate,
      start_time: startTime,
      end_date: endDate,
      end_time: endTime,
    };

    dispatch(rentalPriceDetails(payload))
      .then((response) => {
        if (response?.price) {
          setTotalPrice(response.total_price);
        } else {
          console.error("Price not available in response:", response);
        }
      })
      .catch((error) => {
        console.error("Error calculating price:", error);
      });
  };

  const [isIconHovered, setIsIconHovered] = useState(false);

  return (
    <div className="rent-product">
      <div className="modal-head">
        <h6>Rent Product</h6>
        <button className="btn" onClick={() => setModaltitle(false)}>
          <img src={`${CloudStorage.cloudurl}/Assets/Images/common/x-circle.svg`} alt="" />
        </button>
      </div>

      <h1>{totalprice ? `$ ${Commas(totalprice)}` : ""}</h1>

      <form className="modal-form" onSubmit={formik.handleSubmit}>
        <h6>
          Rental Type{" "} <AsteriskLabel /> {" "}
          <img
            data-tooltip-id="my-tooltip-1"
            src={`${CloudStorage.cloudurl}/Assets/Images/postjob/post-icon.svg`}
            alt=""
          />
        </h6>

        <div className="checkbox-label">
          {data?.rental_only == 1 && (
            <label className="checkbox-container">
              <input
                type="radio"
                name="rental_type"
                value="1"
                checked={formik.values.rental_type == "1"}
                onChange={formik.handleChange}
              />
              <span className="checkmark">Rental Only</span>
            </label>
          )}
          {data?.rental_with_interpreter == 1 && (
            <label className="checkbox-container">
              <input
                type="radio"
                name="rental_type"
                value="2"
                checked={formik.values.rental_type == "2"}
                onChange={formik.handleChange}
              />
              <span className="checkmark">
                Rental with Interpreter
              </span>
            </label>
          )}

          {isIconHovered && (
            <div className="tooltip-sec active">
              <p>
                The owner will provide <span>an expert</span> to interpret the information provided by you and will operate the machine on your behalf.
              </p>
            </div>
          )}
        </div>
        {formik.touched.rental_type && formik.errors.rental_type && <div className="error">{formik.errors.rental_type}</div>}

        <h6 className="pb-3 pt-3">Rental Duration (in UTC)</h6>
        <div className="row">
          <div className="col-md-6 form-group">
            <label>Start Date <AsteriskLabel /></label>
            <input
              type="date"
              name="start_date"
              className="form-control"
              onChange={formik.handleChange}
              value={formik.values.start_date}
              min={formattedDate}
            />
            {formik.touched.start_date && formik.errors.start_date && <div className="error">{formik.errors.start_date}</div>}
          </div>

          <div className="col-md-6 form-group">
            <label>Start Time <AsteriskLabel /></label>
            <TimePickerRSuite
              formik={formik}
              name={`start_time`}
              label=""
            />
            {formik.touched.start_time && formik.errors.start_time && <div className="error">{formik.errors.start_time}</div>}
          </div>

          <div className="col-md-6 form-group">
            <label>End Date <AsteriskLabel /></label>
            <input
              type="date"
              name="end_date"
              className="form-control"
              onChange={formik.handleChange}
              value={formik.values.end_date}
              min={formattedDate}
            />
            {formik.touched.end_date && formik.errors.end_date && <div className="error">{formik.errors.end_date}</div>}
          </div>

          <div className="col-md-6 form-group">
            <label>End Time <AsteriskLabel /></label>
            <TimePickerRSuite
              formik={formik}
              name={`end_time`}
              label=""
            />
            {formik.touched.end_time && formik.errors.end_time && <div className="error">{formik.errors.end_time}</div>}
          </div>
        </div>

        <button type="submit" className="btn w-100 submit-req">
          Submit Request
        </button>
      </form>

      <Tooltip
        id="my-tooltip-1"
        place="bottom"
        content="The owner will provide an expert to interpret the information provided by you and will operate the machine on your behalf."
      />
    </div>
  );
}